/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {CheckIcon, DocumentDuplicateIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import ModalScrollableClose from "components/ModalScrollableClose";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import UiButton, {UiButtonSize, UiButtonStyle} from "pages/Front/components/Ui/components/UiButton";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {consoleLog} from "utils/utility";
import {SellerDuplicateOfferRequest} from "types/entities/SellerReservations";
import UiTabSwitcher, {UiTabSwitcherItem} from "pages/Front/components/Ui/components/UiTabSwitcher";
import SellerAction from "store/seller/SellerAction";
import {toast} from "react-toastify";
import {closeModal} from "helpers/actions";
import {useHistory} from "react-router";

interface SellerDuplicateOfferFormInputs {
    option: number | string;
    reservation_id: number;
}

interface BookingSellerDuplicateOfferModalProps {
    reservation_id: number;
    title?: string;
    options?: UiTabSwitcherItem[];
    defaultOption?: UiTabSwitcherItem;
}

const BookingSellerDuplicateOfferModal = (props: BookingSellerDuplicateOfferModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const history = useHistory();
    const defaultValues = {
        option: props?.defaultOption?.id ? props.defaultOption.id : undefined,
        reservation_id: props.reservation_id,
    };
    const formSchema = yup.object().shape({
        option: yup.string().required(t("Option is required.")),
        reservation_id: yup.number().required(),
    });
    const {
        register,
        handleSubmit,
        setValue,
        watch,
    } = useForm<SellerDuplicateOfferFormInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(formSchema),
    });
    const selectedOption = watch("option");

    const itemsForOptions: UiTabSwitcherItem[] = props?.options || [
        {id: "1", name: "1"},
        {id: "2", name: "2"},
        {id: "3", name: "3"},
    ];

    const onErrorHandler = (data: any) => {
        consoleLog("onErrorHandler", data);
    };

    const onSubmitHandler = (data: SellerDuplicateOfferFormInputs) => {
        consoleLog("onSubmitHandler", data);

        const dataForRequest: SellerDuplicateOfferRequest = {...data};
        dispatch(SellerAction.fetchDuplicateOffer(dataForRequest))
            .then(
                (onResolve) => {
                    if (onResolve.success) {
                        toast.success(t("The offer has been successfully duplicated."));
                        dispatch(closeModal());
                    }
                },
                (onReject) => {
                    toast.error(t("Error upon duplication."));
                },
            ).catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };

    const setSelectedOptionHandler = (value: any) => {
        setValue("option", value);
    };

    return (
        <CardComponent className={"BookingSellerDuplicateOfferModal sm:w-[500px] w-full !p-0 border border-gray-200"}>
            <div
                className={"flex flex-row justify-between w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-100/20 text-gray-900 drop-shadow-sm"}>
                <div className={"flex flex-row items-center"}>
                    <DocumentDuplicateIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                    <h4 className={"font-medium text-gray-700"}>{props?.title || t("Duplicate offer")}</h4>
                </div>
                <ModalScrollableClose />
            </div>

            <div
                className={"flex w-full flex-col px-3 pt-3 pb-4 bg-gray-50 overflow-hidden h-auto rounded-bl-lg rounded-br-lg"}>
                <div className={"flex overflow-y-auto h-full w-full"}>
                    <form className={"w-full gap-3 flex flex-col"}
                          onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                        <CardComponent className={"grid grid-cols-2 gap-3"}>
                            <input type={"hidden"} {...register("reservation_id")} />

                            <UiTabSwitcher
                                className={"col-span-2"}
                                label={t("Select number of copies")}
                                items={itemsForOptions}
                                selected={selectedOption}
                                onClick={id => setSelectedOptionHandler(id)}
                            />

                        </CardComponent>
                        <UiButton
                            className={"w-full"}
                            icon={<CheckIcon className={"w-4 h-4 flex-none"} />}
                            title={t("Execute")}
                            onClick={handleSubmit(onSubmitHandler, onErrorHandler)}
                            style={UiButtonStyle.Add}
                            size={UiButtonSize.Medium}
                        />
                    </form>
                </div>
            </div>
        </CardComponent>
    );
};

export default BookingSellerDuplicateOfferModal;